import { lazy } from 'react';
import { useSelector } from 'react-redux';
import { matchPath } from 'react-router';
import { Navigate } from 'react-router-dom';
import settings from '/@/core/settings';
import { _lazy } from '/@/locale';
import { checkInternal } from '/@/settings/menu.js';
import { getAskAnExpertEnabled } from '/@/store/ask-an-expert/selectors';
import { getBagQualityEnabled } from '/@/store/bag-quality/selectors';
import { getCatalogBrandBySlug } from '/@/store/catalog/selectors';
import { getLibraryEnabled } from '/@/store/library/selectors';
import { getSoyMultipliersEnabled } from '/@/store/multipliers/selectors';
import { getWeedsRecommendationEnabled } from '/@/store/private-data/recommendation-weeds/selectors';
import {
    getAppInstance,
    getCornRecommendationEnabled,
    getEnabledWeedFinder,
    getEnableRvCustomHome,
    getMapsEnabled,
    getSoyRecommendationEnabled,
    getUser,
} from '/@/store/session/selectors';
import { getWeatherFeatureEnabled } from '/@/store/weather/selectors';
import {
    isUserValidatedAdvisor,
    isUserValidatedConsultant,
    isUserValidatedRV,
    isUserValidatedSquad,
    isValidatedBayerUserType,
} from '/@/utils';
import AskAnExpert from '/@/views/ask-an-expert';
import BagQuality from '/@/views/bag-quality-v2';
import CarbonCalculator from '/@/views/carbon-calculator';
import CarbonCalculatorBR from '/@/views/carbon-calculator-BR';
import * as catalog from '/@/views/catalog';
import * as cropMonitoring from '/@/views/crop-monitoring';
import CropProtectionRecommendationDetail from '/@/views/crop-protection-reco-detail';
import CropProtectionRecommendationCreate from '/@/views/crop-protection-recommendation-create';
import GrowerDashboardDetail from '/@/views/dashboard/grower/detail';
import GrowerHome from '/@/views/dashboard/grower/home';
import RVHome from '/@/views/dashboard/rv/home';
import DensityRecommendationCreate from '/@/views/density-recommendation/create';
import CornDensityRecommendationWrapper from '/@/views/density-recommendation/detail';
import emailVerified from '/@/views/email-verified';
import FieldTrialNetwork from '/@/views/field-trial-network';
import * as fields from '/@/views/fields';
import FieldMonitoring from '/@/views/fields/field-monitoring';
import FieldMonitoringReport from '/@/views/fields/field-monitoring/report';
import * as frostRisk from '/@/views/frost-risk';
import * as headToHead from '/@/views/head-to-head';
import * as helpCenter from '/@/views/help-center';
import * as library from '/@/views/library';
import Maps from '/@/views/maps';
import MyRecommendations from '/@/views/my-recommendations';
import NewHome from '/@/views/new-home';
import * as newNitrogenRecommendation from '/@/views/new-nitrogen-recommendation';
import Notifications from '/@/views/notifications';
import * as productDatasheet from '/@/views/product-datasheet';
import ProductExploration from '/@/views/product-exploration';
import ProductExplorationComparator from '/@/views/product-exploration/comparator';
import ProductExplorationDetail from '/@/views/product-exploration/detail';
import ProductExplorationList from '/@/views/product-exploration/list';
import ProductExplorationTechnology from '/@/views/product-exploration/technology';
import CropProtectionWhenToApplyPage from '/@/views/product-exploration/when-to-apply';
import * as recommendations from '/@/views/recommendations';
import * as multipliers from '/@/views/soy-multipliers';
import * as users from '/@/views/users';
import Weather from '/@/views/weather';
import * as weeds from '/@/views/weeds';
import type { NavbarTabKey } from '../components/app-navbar/hooks';
import { useIsValidatedBayerUserType, useIsValidatedConsultant, useIsValidatedSquad } from '../hooks/session';
import { stripLocalePrefix } from '../utils/router';
import AcceptInvitationView from '../views/accept-invitation';
import Activities from '../views/activities';
import MyFieldsListDashboard from '../views/dashboard/consultant/fields';
import ConsultantHome from '../views/dashboard/consultant/home';
import GrowerRecommendationsView from '../views/dashboard/grower/recommendations';
import GrowerReportsView from '../views/dashboard/grower/reports';
import GrowersListDashboard from '../views/dashboard/growers';
import RVsListDashboard from '../views/dashboard/rvs';
import RVDetail from '../views/dashboard/rvs/detail';
import SquadHome from '../views/dashboard/squad/home';
import FieldMonitoringCM from '../views/fields/field-monitoring/dashboard';
import FieldviewLoginRedirectView from '../views/fieldview-login-redirect';
import ProfileV2 from '../views/users/profile-v2';

// Lazy load visit-reports to avoid pulling TipTap dependencies into PDF builds
const VisitReportEdit = lazy(() => import('../views/visit-reports/edit'));
const VisitReportResultWrapper = lazy(() => import('../views/visit-reports/result'));

export const VIEWS_BY_NAME = {};

type ViewType = 'auth' | 'user' | 'internal' | 'catalog' | 'email-verified' | 'public';
export interface ViewConfig {
    name: string;
    title: ReturnType<typeof _lazy>;
    path: string;
    component: React.ComponentType<any>;
    hideAskAnExpert?: boolean;
    appSidebarTabKey?: NavbarTabKey;
    hideSidebar?: boolean;
    type?: ViewType;
}

const validateViews = (views) => {
    const requiredViewKeys = ['name', 'title', 'path', 'component'];
    const validViewTypes: ViewType[] = ['auth', 'user', 'internal', 'catalog', 'email-verified', 'public'];

    views.forEach((view) => {
        if (!view.path.startsWith('/')) {
            throw new Error(`Invalid view path: "${view.path}". Paths must start with a forward slash.`);
        }

        if (view.path.split('/')?.[1]?.length === 2) {
            // Prevent language code conflict
            throw new Error(`Invalid view path: "${view.path}". Path first part can't have only 2 chars.`);
        }

        requiredViewKeys.forEach((key) => {
            if (!view[key]) {
                throw new Error(`Invalid view: "${view.name}". Missing key: "${key}".`);
            }
        });

        if (view.type && !validViewTypes.includes(view.type)) {
            throw new Error(`Invalid view type: "${view.type}".`);
        }
    });
};

export const configureViews = () => {
    const redirectWhenDisabled = (enabled, View, props = {}) => {
        if (enabled) {
            return <View {...props} />;
        }
        return <Navigate to='/users/login/' />;
    };

    const views = [
        {
            name: 'emailVerified',
            title: _lazy('Email Verificado'),
            path: '/email-verified/:token',
            component: emailVerified,
            hideAskAnExpert: true,
            hideSidebar: true,
            type: 'email-verified',
        },
        {
            name: 'fieldview-login-redirect',
            title: _lazy('Login Fieldview'),
            path: '/fieldview-login-redirect/',
            component: FieldviewLoginRedirectView,
            hideAskAnExpert: true,
            hideSidebar: true,
            type: 'public',
        },
        {
            name: 'accept-invitation',
            title: _lazy('Aceptar invitación de RV'),
            path: '/accept-invitation/:token/',
            component: AcceptInvitationView,
            hideAskAnExpert: true,
            hideSidebar: true,
            type: 'public',
        },

        // Users
        {
            name: 'Landing',
            type: 'auth',
            title: _lazy('Landing'),
            path: '/',
            component: () => <Navigate to='/users/login/' />,
            hideSidebar: true,
            hideMenu: true,
            hideAskAnExpert: true,
        },
        {
            name: 'Login',
            type: 'auth',
            title: _lazy('Login'),
            path: '/users/login/',
            component: users.NewLogin,
            hideSidebar: true, // Login is handled from the view
            hideAskAnExpert: true,
        },
        {
            name: 'RequestAccess',
            type: 'auth',
            title: _lazy('Solicitar acceso'),
            path: '/users/request-access/',
            component: ({ props }) => {
                const appInstance = useSelector(getAppInstance);
                const enableFieldviewBlockingModal = appInstance?.enableFieldviewBlockingModal;
                return redirectWhenDisabled(enableFieldviewBlockingModal, users.RequestAccess, props);
            },
            hideSidebar: true,
            hideAskAnExpert: true,
        },
        {
            // Reached from the acceptance email, and directly after an auto-accepted
            // request. The token is validated server-side.
            name: 'CreatePassword',
            type: 'auth',
            title: _lazy('Crear contraseña'),
            path: '/users/create-password/:token/',
            component: ({ props }) => {
                const appInstance = useSelector(getAppInstance);
                const enableFieldviewBlockingModal = appInstance?.enableFieldviewBlockingModal;
                return redirectWhenDisabled(enableFieldviewBlockingModal, users.CreatePassword, props);
            },
            hideSidebar: true,
            hideAskAnExpert: true,
        },
        {
            // The counterpart of `CreatePassword` for an email that already has an
            // account: same entry points, but there is only a password to enter.
            name: 'ExistingAccountLogin',
            type: 'auth',
            title: _lazy('Ya existe una cuenta con este email'),
            path: '/users/existing-account/:token/',
            component: ({ props }) => {
                const appInstance = useSelector(getAppInstance);
                const enableFieldviewBlockingModal = appInstance?.enableFieldviewBlockingModal;
                return redirectWhenDisabled(enableFieldviewBlockingModal, users.ExistingAccountLogin, props);
            },
            hideSidebar: true,
            hideAskAnExpert: true,
        },
        {
            name: 'PasswordResetRequest',
            type: 'auth',
            title: _lazy('Recuperar contraseña'),
            path: '/users/reset-password/',
            // Not the bare form: as a route it needs the brand artwork and a way back
            // that the login page's inline copy gets from its parent.
            component: users.PasswordResetRequestScreen,
            hideSidebar: true,
            hideAskAnExpert: true,
        },
        {
            name: 'PasswordResetConfirm',
            type: 'auth',
            title: _lazy('Recuperar contraseña'),
            path: '/users/reset-password/confirm/',
            component: users.PasswordResetConfirm,
            hideSidebar: true,
            hideAskAnExpert: true,
        },
        {
            name: 'Profile',
            title: _lazy('Perfil'),
            path: '/users/profile/',
            /** @type {{location: {state: {params: {returnUrl?: string}}}}} props */
            component: (props) => <ProfileV2 {...props} />,
            hideAskAnExpert: true,
        },
        {
            name: 'UserPreferences',
            title: _lazy('Ajustes'),
            path: '/users/profile/settings/',
            component: users.UserPreferences,
            hideAskAnExpert: true,
        },
        {
            name: 'PasswordChange',
            type: 'user',
            title: _lazy('Modificar contraseña'),
            path: '/users/change-password/',
            component: users.PasswordChange,
            hideAskAnExpert: true,
        },
        {
            name: 'WeedFinder',
            title: _lazy('Buscador de malezas'),
            path: '/weeds/',
            component: (props) => {
                const enabled = useSelector((state) => getEnabledWeedFinder(state));
                return redirectWhenDisabled(enabled, weeds.WeedFinder, props);
            },
            hideAskAnExpert: false,
            appSidebarTabKey: 'utilities',
        },
        {
            name: 'Activities.List',
            title: _lazy('Actividades'),
            path: '/activities/',
            component: (props) => {
                const isValidatedConsultant = useIsValidatedConsultant();
                const isValidatedSquad = useIsValidatedSquad();
                return redirectWhenDisabled(isValidatedConsultant || isValidatedSquad, Activities, props);
            },
            appSidebarTabKey: 'utilities',
        },
        // New Home
        {
            name: 'NewHome',
            title: _lazy('Home'),
            path: '/home/',
            component: (props) => {
                const user = useSelector(getUser);
                const enableRvCustomHome = useSelector(getEnableRvCustomHome);
                const isValidatedRV = enableRvCustomHome && isUserValidatedRV(user);
                const isValidatedConsultant = enableRvCustomHome && isUserValidatedConsultant(user);
                const isValidatedSquad = enableRvCustomHome && isUserValidatedSquad(user);
                const isValidatedAdvisor = enableRvCustomHome && isUserValidatedAdvisor(user);
                if (settings.app.theme === 'expertobayer') {
                    if (isValidatedAdvisor) {
                        return redirectWhenDisabled(Boolean(user), RVHome, props);
                    }
                    if (isValidatedSquad) {
                        return redirectWhenDisabled(Boolean(user), SquadHome, props);
                    }
                    if (isValidatedConsultant) {
                        return redirectWhenDisabled(Boolean(user), ConsultantHome, props);
                    }
                    if (isValidatedRV) {
                        return redirectWhenDisabled(Boolean(user), RVHome, props);
                    }
                    return redirectWhenDisabled(Boolean(user), GrowerHome, props);
                }

                return redirectWhenDisabled(Boolean(user), NewHome, props);
            },
            hideAskAnExpert: false,
            appSidebarTabKey: 'home',
        },
        // AskAnExpert
        {
            name: 'AskAnExpert',
            title: _lazy('Consulta a un experto'),
            path: '/ask-an-expert/:dataField',
            component: (props) => {
                const enabled = useSelector(getAskAnExpertEnabled);
                return redirectWhenDisabled(enabled, AskAnExpert, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'utilities',
        },
        // Bag Quality
        {
            type: 'public',
            name: 'BagQuality',
            title: _lazy('Calidad de semilla'),
            path: '/bag-quality/',
            component: (props) => {
                const enabled = useSelector(getBagQualityEnabled);
                return redirectWhenDisabled(enabled, BagQuality, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'utilities',
        },
        // Weather
        {
            name: 'Weather',
            title: _lazy('Weather'),
            path: '/weather/:fieldId',
            component: (props) => {
                const enabled = useSelector(getWeatherFeatureEnabled);
                return redirectWhenDisabled(enabled, Weather, props);
            },
            hideAskAnExpert: false,
        },
        // Multipliers
        {
            name: 'SoyMultipliers',
            title: _lazy('Multiplicadores soja'),
            path: '/soy-multipliers/',
            component: (props) => {
                const enabled = useSelector(getSoyMultipliersEnabled);
                return redirectWhenDisabled(enabled, multipliers.SoyMultipliers, props);
            },
            hideAskAnExpert: false,
            appSidebarTabKey: 'utilities',
        },
        {
            name: 'SoyMultipliersDetail',
            title: _lazy('Multiplicadores soja detalle'),
            path: '/soy-multipliers/detail/:type/:brand/:zoneId/:profileId',
            component: (props) => {
                const enabled = useSelector(getSoyMultipliersEnabled);
                return redirectWhenDisabled(enabled, multipliers.SoyMultipliersDetail, props);
            },
            hideAskAnExpert: false,
            appSidebarTabKey: 'utilities',
        },
        // Fields
        {
            name: 'Home',
            title: _lazy('Inicio'),
            path: '/fields/',
            component: (props) => {
                const user = useSelector(getUser);
                const enableRvCustomHome = useSelector(getEnableRvCustomHome);
                if (settings.app.theme === 'expertobayer' || enableRvCustomHome) {
                    return <Navigate to='/home/' />;
                }

                return redirectWhenDisabled(Boolean(user), fields.FieldList, props);
            },
            hideAskAnExpert: false,
            appSidebarTabKey: 'home',
        },
        {
            name: 'fields.NewField',
            title: _lazy('Nuevo lote'),
            path: '/fields/new/',
            component: (props) => {
                if (settings.app.theme === 'expertobayer') return <fields.NewFieldV2 {...props} />;
                return <fields.NewFieldV1 {...props} />;
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'growers',
        },
        {
            name: 'fields.FieldDetail',
            title: _lazy('Detalles del lote'),
            path: '/fields/:fieldId/',
            component: (props) => {
                const enabled = settings.app.theme === 'expertobayer';
                return redirectWhenDisabled(enabled, FieldMonitoring, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'growers',
        },
        {
            name: 'Map',
            title: _lazy('Mapas'),
            path: '/map/',
            component: (props) => {
                const enabled = useSelector(getMapsEnabled);
                return redirectWhenDisabled(enabled, Maps, props);
            },
            hideAskAnExpert: false,
        },
        {
            name: 'frost_risk',
            title: _lazy('Mapas de riesgo de helada'),
            path: '/frost-risk/',
            component: (props) => {
                return redirectWhenDisabled(true, frostRisk.FrostRiskLanding, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'utilities',
        },
        {
            name: 'frost_risk_map',
            title: _lazy('Mapas de riesgo de helada'),
            path: '/frost-risk-map/',
            component: (props) => {
                return redirectWhenDisabled(true, frostRisk.FrostRiskView, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'utilities',
        },
        {
            name: 'recommendations.List',
            title: _lazy('Mis recomendaciones'),
            path: '/recommendations/',
            component: GrowerRecommendationsView,
            hideAskAnExpert: true,
            appSidebarTabKey: 'recommendations',
        },

        {
            name: 'recommendations.RecommendationEdit',
            title: _lazy('Configurar recomendación'),
            path: '/recommendations/:crop/:fieldId/edit/',
            component: (props) => {
                const appInstance = useSelector(getAppInstance);
                let enabled = true;
                if (props.crop === 'corn') {
                    enabled = useSelector((state) => getCornRecommendationEnabled(state, appInstance));
                } else if (props.crop === 'soy') {
                    enabled = useSelector((state) => getSoyRecommendationEnabled(state, appInstance));
                }

                return redirectWhenDisabled(enabled, recommendations.RecommendationAIO, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'utilities',
        },
        {
            name: 'recommendations.RecommendationResult',
            title: _lazy('Recomendación'),
            path: '/recommendations/:recommendationId/:crop/',
            component: (props) => {
                const appInstance = useSelector(getAppInstance);

                let enabled = true;
                if (props.crop === 'corn') {
                    enabled = useSelector((state) => getCornRecommendationEnabled(state, appInstance));
                } else if (props.crop === 'soy') {
                    enabled = useSelector((state) => getSoyRecommendationEnabled(state, appInstance));
                }

                return redirectWhenDisabled(enabled, recommendations.RecommendationOnlyRead, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'utilities',
        },
        {
            name: 'recommendations.RecommendationResultV2',
            title: _lazy('Recomendación'),
            path: '/recommendations/v2/:recommendationId/:crop/',
            component: (props) => {
                const appInstance = useSelector(getAppInstance);

                let enabled = true;
                if (props.crop === 'corn') {
                    enabled = useSelector((state) => getCornRecommendationEnabled(state, appInstance));
                } else if (props.crop === 'soy') {
                    enabled = useSelector((state) => getSoyRecommendationEnabled(state, appInstance));
                }

                return redirectWhenDisabled(enabled, recommendations.RecommendationResultV2, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'utilities',
        },
        {
            name: 'recommendations.RecommendationFieldSelect',
            title: _lazy('Recomendación'),
            path: '/recommendations/field-select/:crop/',
            component: (props) => {
                const appInstance = useSelector(getAppInstance);

                let enabled = true;
                if (props.crop === 'corn') {
                    enabled = useSelector((state) => getCornRecommendationEnabled(state, appInstance));
                } else if (props.crop === 'soy') {
                    enabled = useSelector((state) => getSoyRecommendationEnabled(state, appInstance));
                }

                return redirectWhenDisabled(enabled, recommendations.RecommendationFieldSelect, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'utilities',
        },
        {
            name: 'recommendations.RecommendationWeeds',
            title: _lazy('Recomendación de Malezas'),
            path: '/recommendation-weeds/:id/:field/',
            component: (props) => {
                const enabled = useSelector(getWeedsRecommendationEnabled);
                return redirectWhenDisabled(enabled, recommendations.RecommendationWeeds, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'utilities',
        },
        {
            name: 'MyRecommendations',
            title: _lazy('Mis recomendaciones'),
            path: '/my-recommendations/',
            component: MyRecommendations,
            hideAskAnExpert: true,
            appSidebarTabKey: 'utilities',
        },

        // Head to head
        {
            name: 'headToHead.HeadToHead',
            title: _lazy('Head to head'),
            path: '/fields/:fieldId/head-to-head/:crop/',
            component: (props) => {
                const appInstance = useSelector(getAppInstance);
                const enabled =
                    (appInstance.enableCornHeadToHead || appInstance.enableSoyHeadToHead) &&
                    checkInternal(useSelector(getUser), appInstance);
                return redirectWhenDisabled(enabled, headToHead.FieldHeadToHead, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'utilities',
        },
        // Library
        {
            name: 'library.LibraryList',
            title: _lazy('Biblioteca'),
            path: '/library/',
            component: (props) => {
                const enabled = useSelector(getLibraryEnabled);
                return redirectWhenDisabled(enabled, library.LibraryList, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'utilities',
        },
        {
            name: 'library.LibraryDetail',
            title: _lazy('Biblioteca'),
            path: '/library/items/:slug/',
            type: 'public',
            component: (props) => {
                const enabled = useSelector(getLibraryEnabled);
                return redirectWhenDisabled(enabled, library.LibraryDetail, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'utilities',
        },
        {
            name: 'helpCenter.Home',
            title: _lazy('Ayuda'),
            path: '/help-center/',
            component: (props) => redirectWhenDisabled(true, helpCenter.HelpCenterHome, props),
            hideAskAnExpert: true,
            appSidebarTabKey: 'helpCenter',
        },
        {
            name: 'helpCenter.Article',
            title: _lazy('Ayuda'),
            path: '/help-center/pages/:slug/',
            component: (props) => redirectWhenDisabled(true, helpCenter.HelpCenterArticle, props),
            hideAskAnExpert: true,
            appSidebarTabKey: 'helpCenter',
        },

        // Crop Monitoring
        {
            // redirect
            name: 'cropMonitoring.CropMonitoring',
            title: _lazy('Seguimiento de Cultivo'),
            path: '/crop-monitoring',
            component: (props) => {
                const enabled = useSelector(getAppInstance).enableCropMonitoring;
                return redirectWhenDisabled(enabled, cropMonitoring.CropMonitoring, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'home',
        },
        {
            name: 'cropMonitoring.CropMonitoringCorn',
            title: _lazy('Nuevo Seguimiento de Cultivo'),
            path: '/crop-monitoring/corn',
            component: (props) => {
                const enabled = useSelector(getAppInstance).enableCropMonitoring;
                return redirectWhenDisabled(enabled, cropMonitoring.CropMonitoringCorn, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'home',
        },
        {
            name: 'cropMonitoring.CornCropMonitoringDetail',
            title: _lazy('Seguimiento de Cultivo'),
            path: '/crop-monitoring/corn/:cropMonitoringId/',
            component: (props) => {
                const enabled = useSelector(getAppInstance).enableCropMonitoring;
                return redirectWhenDisabled(enabled, FieldMonitoringCM, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'home',
        },
        {
            name: 'cropMonitoring.CornCropMonitoringDetail.report',
            title: _lazy('Detalles del lote'),
            path: '/crop-monitoring/corn/:cropMonitoringId/report/',
            component: (props) => {
                const enabled = useSelector(getAppInstance).enableCropMonitoringReport;
                return redirectWhenDisabled(enabled, FieldMonitoringReport, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'home',
        },
        {
            name: 'cropMonitoring.CropMonitoringStepperMobile',
            title: _lazy('Nuevo Seguimiento de Cultivo'),
            path: '/crop-monitoring-stepper',
            component: (props) => {
                const enabled = useSelector(getAppInstance).enableCropMonitoring;
                return redirectWhenDisabled(enabled, cropMonitoring.CropMonitoringStepperMobile, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'home',
        },
        // Catalog
        {
            type: 'catalog',
            name: 'catalog.BrandSelection',
            title: _lazy('Catálogo'),
            path: '/catalog/',
            component: (props) => {
                return redirectWhenDisabled(true, catalog.BrandSelection, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'products',
        },
        {
            type: 'catalog',
            name: 'catalog.CropProtection.Acceleron',
            title: _lazy('Acceleron'),
            path: '/catalog/crop-protection/acceleron',
            component: (props) => {
                return redirectWhenDisabled(true, catalog.CropProtection.Acceleron, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'products',
        },
        {
            type: 'catalog',
            name: 'catalog.AcceleronFactors',
            title: _lazy('Acceleron'),
            path: '/catalog/crop-protection/acceleron/:id/',
            component: (props) => {
                return redirectWhenDisabled(true, catalog.AcceleronFactors, props);
            },
            hideAskAnExpert: false,
            appSidebarTabKey: 'products',
        },
        {
            type: 'catalog',
            name: 'catalog.Biotechnology',
            title: _lazy('Catálogo'),
            path: '/catalog/:type/biotechnology/',
            component: (props) => {
                return redirectWhenDisabled(true, catalog.Biotechnology, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'products',
        },
        {
            type: 'catalog',
            name: 'catalog.ProductSelection',
            title: _lazy('Catálogo'),
            path: '/catalog/:type/:slug/:internal',
            component: (props) => {
                const brand = useSelector((state) => getCatalogBrandBySlug(state, props.slug));

                if (!brand) return <Navigate to='/catalog/' />;

                return redirectWhenDisabled(true, catalog.NewProductSelection, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'products',
        },
        {
            type: 'catalog',
            name: 'catalog.ProductDetail',
            title: _lazy('Catálogo'),
            path: '/catalog/:type/:slug/:zoneId/:itemId/:internal',
            component: (props) => {
                const brand = useSelector((state) => getCatalogBrandBySlug(state, props.slug));

                if (!brand) return <Navigate to='/catalog/' />;

                return redirectWhenDisabled(true, catalog.NewProductDetail, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'products',
        },
        // RV dashboard
        {
            name: 'RVs.list',
            title: _lazy('RVs'),
            path: '/dashboards/rvs/',
            component: (props) => {
                const user = useSelector(getUser);
                const enableRvCustomHome = useSelector(getEnableRvCustomHome);
                const isValidatedConsultant = enableRvCustomHome && isUserValidatedConsultant(user);
                return redirectWhenDisabled(isValidatedConsultant, RVsListDashboard, props);
            },
            appSidebarTabKey: 'rvs',
        },
        {
            name: 'RVs.detail',
            title: _lazy('Detalle del RV'),
            path: '/dashboards/rvs/:rvId/',
            component: (props) => {
                return redirectWhenDisabled(true, RVDetail, props);
            },
            appSidebarTabKey: 'rvs',
        },
        {
            name: 'MyFields.list',
            title: _lazy('Mis Lotes'),
            path: '/dashboards/my-fields/',
            component: (props) => {
                const isBayer = useIsValidatedBayerUserType();
                return redirectWhenDisabled(isBayer, MyFieldsListDashboard, props);
            },
            appSidebarTabKey: 'fields',
        },
        // Grower dashboard
        {
            name: 'Growers.list',
            title: _lazy('Productores'),
            path: '/dashboards/growers/',
            component: (props) => {
                // TODO: Add permission check
                const user = useSelector(getUser);
                const enableRvCustomHome = useSelector(getEnableRvCustomHome);
                const isValidatedRV = enableRvCustomHome && isUserValidatedRV(user);
                const isValidatedAdvisor = enableRvCustomHome && isUserValidatedAdvisor(user);

                return redirectWhenDisabled(isValidatedRV || isValidatedAdvisor, GrowersListDashboard, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'growers',
        },
        {
            name: 'Growers.detail',
            title: _lazy('Productor'),
            path: '/dashboards/growers/:growerNodeId/',
            component: (props) => {
                // TODO: Add permission check
                const user = useSelector(getUser);
                const enableRvCustomHome = useSelector(getEnableRvCustomHome);
                const isValidatedUserType = enableRvCustomHome && isValidatedBayerUserType(user);

                return redirectWhenDisabled(isValidatedUserType, GrowerDashboardDetail, {
                    ...props,
                    growerNodeId: props.growerNodeId,
                });
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'growers',
        },
        // RV Product Exploration
        {
            name: 'productExploration.main',
            title: _lazy('Exploración de productos'),
            path: '/product-exploration/',
            component: (props) => {
                const user = useSelector(getUser);
                const enableRvCustomHome = useSelector(getEnableRvCustomHome);
                const isValidatedUserType = enableRvCustomHome && isValidatedBayerUserType(user);

                return redirectWhenDisabled(isValidatedUserType, ProductExploration, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'products',
        },
        {
            name: 'productExploration.list',
            title: _lazy('Lista de productos'),
            path: '/product-exploration/:type/:slug/',
            component: (props) => {
                const user = useSelector(getUser);
                const enableRvCustomHome = useSelector(getEnableRvCustomHome);
                const isValidatedUserType = enableRvCustomHome && isValidatedBayerUserType(user);

                return redirectWhenDisabled(isValidatedUserType, ProductExplorationList, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'products',
        },
        {
            name: 'productExploration.comparator',
            title: _lazy('Comparador de productos'),
            path: '/product-exploration/comparator/:catalogType/:productIds/',
            component: (props) => {
                const user = useSelector(getUser);
                const enableRvCustomHome = useSelector(getEnableRvCustomHome);
                const isValidatedUserType = enableRvCustomHome && isValidatedBayerUserType(user);

                return redirectWhenDisabled(isValidatedUserType, ProductExplorationComparator, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'products',
        },
        {
            name: 'productExploration.detail',
            title: _lazy('Detalle de producto'),
            path: '/product-exploration/:type/:slug/:productId/',
            component: (props) => {
                const user = useSelector(getUser);
                const enableRvCustomHome = useSelector(getEnableRvCustomHome);
                const isValidatedUserType = enableRvCustomHome && isValidatedBayerUserType(user);

                return redirectWhenDisabled(isValidatedUserType, ProductExplorationDetail, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'products',
        },
        {
            name: 'productExploration.technology',
            title: _lazy('Tecnologías'),
            path: '/product-exploration/technology/',
            component: (props) => {
                const user = useSelector(getUser);
                const enableRvCustomHome = useSelector(getEnableRvCustomHome);
                const isValidatedUserType = enableRvCustomHome && isValidatedBayerUserType(user);

                return redirectWhenDisabled(isValidatedUserType, ProductExplorationTechnology, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'products',
        },
        {
            name: 'productExploration.cropProtection.whenToApply',
            title: _lazy('Cuándo aplicar un fitosanitario según el cultivo'),
            path: '/product-exploration/when-to-apply/',
            component: (props) => {
                const user = useSelector(getUser);
                const enableRvCustomHome = useSelector(getEnableRvCustomHome);
                const isValidatedUserType = enableRvCustomHome && isValidatedBayerUserType(user);

                return redirectWhenDisabled(isValidatedUserType, CropProtectionWhenToApplyPage, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'products',
        },
        // Product Datasheet
        {
            name: 'productDatasheet.ProductDatasheetBrandSelection',
            title: _lazy('Ficha técnica de producto'),
            path: '/product-datasheet',
            component: (props) => {
                const appInstance = useSelector(getAppInstance);
                const enabled = appInstance.enableProductDatasheet && checkInternal(useSelector(getUser), appInstance);
                return redirectWhenDisabled(enabled, productDatasheet.ProductDatasheetBrandSelection, props);
            },
            hideAskAnExpert: true,
        },
        {
            name: 'productDatasheet.ProductDatasheetProductsConfiguration',
            title: _lazy('Configuración de productos'),
            path: '/product-datasheet/:type/:slug',
            component: (props) => {
                const appInstance = useSelector(getAppInstance);
                const enabled = appInstance.enableProductDatasheet && checkInternal(useSelector(getUser), appInstance);
                return redirectWhenDisabled(enabled, productDatasheet.ProductDatasheetProductsConfiguration, props);
            },
            hideAskAnExpert: true,
        },
        {
            name: 'productDatasheet.ProductDatasheetPreview',
            title: _lazy('Previsualización de ficha técnica de producto'),
            path: '/product-datasheet-preview',
            component: (props) => {
                const appInstance = useSelector(getAppInstance);
                const enabled = appInstance.enableProductDatasheet && checkInternal(useSelector(getUser), appInstance);
                return redirectWhenDisabled(enabled, productDatasheet.ProductDatasheetPreview, props);
            },
            hideAskAnExpert: true,
        },
        // Nitrogen Recommendation
        {
            // redirect
            name: 'nitrogenRecommendation.NitrogenRecommendationList',
            title: _lazy('Nitrógeno Smart'),
            path: '/nitrogeno-smart/',
            component: (props) => {
                const enabled = useSelector(getAppInstance).enableNitogrenRecommendation;
                return redirectWhenDisabled(enabled, newNitrogenRecommendation.NitrogenRecommendationList, props);
            },
            hideAskAnExpert: false,
            appSidebarTabKey: 'utilities',
        },
        {
            name: 'nitrogenRecommendation.NitrogenRecommendationSuccess',
            title: _lazy('Success'),
            path: '/nitrogeno-smart/success/:id',
            component: (props) => {
                const enabled = useSelector(getAppInstance).enableNitogrenRecommendation;
                return redirectWhenDisabled(enabled, newNitrogenRecommendation.NitrogenRecommendationSuccess, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'utilities',
        },
        {
            name: 'nitrogenRecommendation.NitrogenRecommendationCreate',
            title: _lazy('Nitrógeno Smart'),
            path: '/nitrogeno-smart/create/:fieldId?',
            component: (props) => {
                const enabled = useSelector(getAppInstance).enableNitogrenRecommendation;
                return redirectWhenDisabled(enabled, newNitrogenRecommendation.NitrogenRecommendationForm, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'utilities',
        },
        {
            name: 'nitrogenRecommendation.NitrogenRecommendationDetail',
            title: _lazy('Nitrógeno Smart'),
            path: '/nitrogeno-smart/:id/',
            component: (props) => {
                const enabled = useSelector(getAppInstance).enableNitogrenRecommendation;
                return redirectWhenDisabled(enabled, newNitrogenRecommendation.NitrogenRecommendationDetail, props);
            },
            hideAskAnExpert: false,
            appSidebarTabKey: 'utilities',
        },

        // Fieldtrials Network -------------------------------------------------------
        {
            // redirect
            name: 'FieldTrialNetwork',
            title: _lazy('Red de ensayos'),
            path: '/field-trial-network/',
            component: (props) => {
                const appInstance = useSelector(getAppInstance);
                const enabled = appInstance.enableFieldTrialNetwork && checkInternal(useSelector(getUser), appInstance);
                return redirectWhenDisabled(enabled, FieldTrialNetwork, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'utilities',
        },
        // Carbon Calculator -------------------------------------------------------
        {
            // redirect
            name: 'CarbonCalculator',
            title: _lazy('Simulador de rotaciones sustentables'),
            path: '/sustainable-rotations-simulator/:fieldId/',
            component: (props) => {
                const enabled = useSelector(getAppInstance).enableCarbonCalculator;
                return redirectWhenDisabled(enabled, CarbonCalculator, props);
            },
            hideAskAnExpert: false,
            appSidebarTabKey: 'utilities',
        },
        {
            name: 'CarbonCalculatorBR',
            title: _lazy('Simulador de rotaciones sustentables'),
            path: '/sustainable-rotations-simulator-BR/:fieldId/',
            component: (props) => {
                const enabled = useSelector(getAppInstance).enableCarbonCalculatorBr;
                return redirectWhenDisabled(enabled, CarbonCalculatorBR, props);
            },
            hideAskAnExpert: false,
            appSidebarTabKey: 'utilities',
        },
        // Notifications --------------------------------------------------------------
        {
            name: 'notifications',
            title: _lazy('Mensajes'),
            path: '/notifications/',
            component: Notifications,
            hideAskAnExpert: false,
        },
        {
            name: 'cropProtectionRecommendation',
            title: _lazy('Recomendación de Protección de Cultivo'),
            path: '/crop-protection/:recommendationId?/',
            component: CropProtectionRecommendationCreate,
            hideAskAnExpert: true,
            appSidebarTabKey: 'utilities',
        },
        {
            name: 'cropProtectionRecommendationDetail',
            title: _lazy('Recomendación de Protección de Cultivo'),
            path: '/crop-protection/detail/:recommendationId/',
            component: CropProtectionRecommendationDetail,
            hideAskAnExpert: true,
            appSidebarTabKey: 'home',
        },
        // Density Recommendation -------------------------------------------------------
        {
            name: 'densityRecommendation.DensityRecommendationCreate',
            title: _lazy('Ajustar la densidad de siembra'),
            path: '/density-recommendation/create/',
            component: DensityRecommendationCreate,
            hideAskAnExpert: true,
            appSidebarTabKey: 'utilities',
        },
        {
            name: 'recommendations.CornDensityRecommendation',
            title: _lazy('Densidad de semilla'),
            path: '/corn-density-recommendation/:recommendationId?/',
            component: CornDensityRecommendationWrapper,
            hideAskAnExpert: true,
            appSidebarTabKey: 'home',
        },
        {
            name: 'reports.List',
            title: _lazy('Reportes'),
            path: '/reports/',
            component: GrowerReportsView,
            hideAskAnExpert: true,
            appSidebarTabKey: 'reports',
        },
        // Vistit Report --------------------------------------------------------------
        {
            name: 'visitReport.VisitReportEdit',
            title: _lazy('Reportes de visita'),
            path: '/visit-report/:visitReportId?/',
            component: (props) => {
                const user = useSelector(getUser);
                const enableRvCustomHome = useSelector(getEnableRvCustomHome);
                const isValidatedUserType = enableRvCustomHome && isValidatedBayerUserType(user);
                return redirectWhenDisabled(isValidatedUserType, VisitReportEdit, props);
            },
            hideAskAnExpert: true,
            appSidebarTabKey: 'home',
        },
        {
            name: 'visitReport.VisitReportDetail',
            title: _lazy('Reportes de visita'),
            path: '/visit-report/detail/:visitReportId?/',
            component: VisitReportResultWrapper,
            hideAskAnExpert: true,
            appSidebarTabKey: 'home',
        },
    ] as const satisfies ViewConfig[];

    validateViews(views);

    views.forEach((view) => {
        if (!view.config) view.config = {};
        VIEWS_BY_NAME[view.name] = view;
    });

    return {
        views,
        viewsByName: VIEWS_BY_NAME,
    };
};

export const getViewByPathname = (pathname: string, views?: ReturnType<typeof configureViews>['views']) => {
    const cleanPathname = stripLocalePrefix(pathname);
    const viewsToUse = views ?? configureViews().views;
    return viewsToUse.find(({ path }: { path: string }) => matchPath({ path }, cleanPathname));
};
